import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { apiClient } from '../../../services/api';

/**
 * App-level registry of in-flight imports.
 *
 * The point of this living above the importer component is that the user can
 * leave the page.  Component state cannot survive a route change, let alone a
 * reload, so "am I still importing?" is answered by the server: the poll below
 * reads `/api/imports/active/`, which lists the organisation's queued and
 * processing `ImportBatch` rows.
 *
 * Scoped per organisation rather than per user, so a colleague's running
 * import is visible too — the worker is shared, and two large imports at once
 * is a thing worth seeing before you start the second one.
 */

export interface ActiveImport {
  batch_id: number;
  dataset: string;
  dataset_label: string;
  status_url: string;
  file_name: string;
  status: 'queued' | 'processing';
  row_count: number;
  processed: number;
  stage: string;
  started_at: string | null;
  started_by: string;
  is_mine: boolean;
  /**
   * The batch claims to be in flight but has stopped moving — a worker that
   * dies mid-run leaves it "processing" indefinitely. The backend has always
   * sent this; the indicators ignored it and span forever on work that was
   * not happening.
   */
  stalled: boolean;
}

/** An import that was running a moment ago and is now gone from the poll. */
export interface FinishedImport {
  batch_id: number;
  dataset: string;
  dataset_label: string;
  file_name: string;
  finished_at: number;
}

/**
 * The spec-document batch shape the importer's `latest` endpoint returns.
 *
 * Spec-PDF imports (products-sold) live in their own `SpecDocumentBatch` model,
 * not in `ImportBatch`, so `/api/imports/active/` never sees them. To flag an
 * ongoing spec import on other pages the same way a spreadsheet import is
 * flagged, the provider additionally polls the spec importer's `latest`
 * endpoint (opt-in via `specBatchLatestUrl`) and folds its in-flight / finished
 * batch into the same `activeImports` / `finishedImports` the ticker reads.
 * Only the handful of fields the ticker needs are consumed here.
 */
export interface SpecBatchLatest {
  batch_id: number | null;
  status: string;
  complete: boolean;
  total_count: number;
  processed_count: number;
}

interface ImportJobContextValue {
  activeImports: ActiveImport[];
  /**
   * Imports that disappeared from the poll within the last few seconds.
   *
   * Without this, a finished import is announced only by its indicator
   * vanishing — the user has to notice an absence and infer success from it,
   * which is indistinguishable from the ticker having failed to load.
   */
  finishedImports: FinishedImport[];
  /** Nudge the poll immediately, e.g. straight after starting an import. */
  refresh: () => void;
  /**
   * Register interest while a component that cares is on screen.
   *
   * Returns an unsubscribe. Without this the provider wraps the whole
   * authenticated shell and polls forever, for every user, whether or not
   * they ever open an importer — a permanent baseline of requests to support
   * a ticker that is almost always showing nothing.
   */
  watch: () => () => void;
}

const ImportJobContext = createContext<ImportJobContextValue>({
  activeImports: [],
  finishedImports: [],
  refresh: () => {},
  // Outside a provider there is nothing to watch; the unsubscribe is a no-op
  // so a consumer can call it unconditionally on unmount.
  watch: () => () => {},
});

/** Idle cadence. Deliberately slow: this is a background awareness poll. */
const IDLE_POLL_MS = 30_000;
/** Faster cadence while something is actually running. */
const ACTIVE_POLL_MS = 6_000;
/** How long a finished import stays announced before it clears itself. */
const FINISHED_VISIBLE_MS = 12_000;

/**
 * Synthetic dataset identity for a spec-document batch in the merged arrays.
 *
 * Deliberately *not* a key in `datasetLocations`, so the navbar progress rings
 * (which filter by dataset against that map) ignore it — the spec import shows
 * in the floating ticker, which lists every active job, without changing which
 * routes/tabs grow a ring. The label is what the ticker renders.
 */
const SPEC_DATASET = 'product-specs';
const SPEC_LABEL = 'Specification documents';

export const ImportJobProvider: React.FC<{
  /** Accepts either form: AuthContext stores a number, most callers a string. */
  organisationId: string | number | null;
  /**
   * The spec-document importer's `latest` endpoint, e.g.
   * `/api/products/spec-documents/latest/`. Supplied only where the spec-PDF
   * importer exists; when omitted the provider polls nothing extra and behaves
   * exactly as the spreadsheet-only ticker always has.
   */
  specBatchLatestUrl?: string;
  children: React.ReactNode;
}> = ({
  organisationId: rawOrganisationId,
  specBatchLatestUrl,
  children,
}) => {
  const organisationId =
    rawOrganisationId === null || rawOrganisationId === undefined
      ? null
      : String(rawOrganisationId);
  const [activeImports, setActiveImports] = useState<ActiveImport[]>([]);
  const [finishedImports, setFinishedImports] = useState<FinishedImport[]>([]);
  // Spec-document batches are tracked in their own state, from their own
  // endpoint, and merged into the public arrays below. Kept separate so the
  // ImportBatch poll above stays untouched — the ticker behaves identically for
  // every spreadsheet importer whether or not a spec batch is in flight.
  const [specActiveImports, setSpecActiveImports] = useState<ActiveImport[]>([]);
  const [specFinishedImports, setSpecFinishedImports] = useState<FinishedImport[]>(
    [],
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);
  // What the previous poll saw, so a disappearance can be detected.
  const seenRef = useRef<ActiveImport[]>([]);
  // The spec batch seen in flight on the previous poll, so its completion can
  // be announced once (then cleared) rather than on every subsequent tick.
  const seenSpecRef = useRef<SpecBatchLatest | null>(null);

  const fetchActive = useCallback(async () => {
    if (!organisationId) {
      setActiveImports([]);
      return [] as ActiveImport[];
    }
    try {
      const data = await apiClient.get<{ imports: ActiveImport[] }>(
        '/api/imports/active/',
        { organisation_id: organisationId },
      );
      const imports = data.imports || [];
      if (!cancelledRef.current) {
        // Anything present last time and absent now has finished. Stalled
        // jobs are excluded: they stop being reported once dismissed, and
        // announcing that as a completion would be a lie.
        const now = Date.now();
        const ids = new Set(imports.map((job) => job.batch_id));
        const justFinished = seenRef.current
          .filter((job) => !ids.has(job.batch_id) && !job.stalled)
          .map((job) => ({
            batch_id: job.batch_id,
            dataset: job.dataset,
            dataset_label: job.dataset_label,
            file_name: job.file_name,
            finished_at: now,
          }));

        if (justFinished.length > 0) {
          setFinishedImports((prev) => [...prev, ...justFinished]);
        }
        setFinishedImports((prev) =>
          prev.filter((job) => now - job.finished_at < FINISHED_VISIBLE_MS),
        );
        seenRef.current = imports;
        setActiveImports(imports);
      }
      return imports;
    } catch {
      // Silent: an awareness poll must never surface an error banner. If the
      // endpoint is unreachable the ticker simply shows nothing.
      return [] as ActiveImport[];
    }
  }, [organisationId]);

  // Normalise a spec-document batch into the shape the ticker already renders.
  // `stalled: false` always — the spec importer has no stall/dismiss path, and
  // StalledImportNotice/dismiss-stalled operate on ImportBatch ids, so a spec
  // batch must never reach them.
  const specToActive = useCallback(
    (batch: SpecBatchLatest): ActiveImport => ({
      batch_id: batch.batch_id as number,
      dataset: SPEC_DATASET,
      dataset_label: SPEC_LABEL,
      status_url: '',
      file_name: '',
      status: 'processing',
      row_count: batch.total_count || 0,
      processed: batch.processed_count || 0,
      stage: '',
      started_at: null,
      started_by: '',
      is_mine: true,
      stalled: false,
    }),
    [],
  );

  // Poll the spec importer's `latest` endpoint and fold its in-flight / just-
  // finished batch into the merged arrays. Returns whether a spec batch is
  // currently in flight, so the loop keeps its active cadence for it too.
  // Opt-in: without `specBatchLatestUrl` this is a no-op and issues no request.
  const fetchSpec = useCallback(async (): Promise<boolean> => {
    if (!organisationId || !specBatchLatestUrl) return false;
    try {
      const data = await apiClient.get<SpecBatchLatest>(specBatchLatestUrl, {
        organisation_id: organisationId,
      });
      if (cancelledRef.current) return false;
      const now = Date.now();
      const inFlight =
        !!data &&
        data.batch_id != null &&
        data.complete === false &&
        data.status === 'processing';
      const prev = seenSpecRef.current;
      if (inFlight) {
        seenSpecRef.current = data;
        setSpecActiveImports([specToActive(data)]);
        return true;
      }
      // Not in flight now. If it was last time, that transition is a
      // completion — announce it once, keyed off the batch we had been
      // watching, then stop tracking so it is not re-announced every tick.
      setSpecActiveImports([]);
      if (prev && prev.batch_id != null) {
        const finished: FinishedImport = {
          batch_id: prev.batch_id,
          dataset: SPEC_DATASET,
          dataset_label: SPEC_LABEL,
          file_name: '',
          finished_at: now,
        };
        setSpecFinishedImports((list) => [...list, finished]);
        seenSpecRef.current = null;
      }
      setSpecFinishedImports((list) =>
        list.filter((job) => now - job.finished_at < FINISHED_VISIBLE_MS),
      );
      return false;
    } catch {
      // Same contract as the ImportBatch poll: an awareness poll never raises
      // an error banner. The in-importer SpecPdfStep poller owns the
      // actionable "lost contact" message for the person actually waiting.
      return false;
    }
  }, [organisationId, specBatchLatestUrl, specToActive]);

  // How many mounted components are actively interested. An importer on
  // screen counts; the navbar ticker alone does not, because with nothing
  // running it has nothing to show.
  const watchersRef = useRef(0);
  const wakeRef = useRef<((force?: boolean) => void) | null>(null);

  const watch = useCallback(() => {
    watchersRef.current += 1;
    // A newly mounted importer wants current state now, and restarts the loop
    // if it had gone quiet.
    wakeRef.current?.();
    return () => {
      watchersRef.current = Math.max(0, watchersRef.current - 1);
    };
  }, []);

  // Self-rescheduling loop rather than setInterval, so the cadence can follow
  // whether anything is actually running without tearing down the effect.
  useEffect(() => {
    cancelledRef.current = false;
    let stopped = false;

    let idle = false;
    // One fetch at a time. A forced tick that arrives mid-fetch asks for one
    // more pass rather than racing it, since the in-flight answer may predate
    // the import that prompted the nudge.
    let inFlight = false;
    let again = false;

    const tick = async () => {
      if (stopped) return;
      if (inFlight) {
        again = true;
        return;
      }
      inFlight = true;
      // A forced tick replaces whatever was scheduled, so the loop never runs
      // two timers at once.
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      let imports = await fetchActive();
      // Polled in the same tick as the ImportBatch poll so there is one
      // cadence, one loop, and one set of watch/refresh semantics across both.
      let specBusy = await fetchSpec();
      while (again && !stopped) {
        again = false;
        imports = await fetchActive();
        specBusy = await fetchSpec();
      }
      inFlight = false;
      if (stopped) return;

      const busy = imports.length > 0 || specBusy;
      // Nothing running and nobody looking: stop rather than idle forever.
      // One fetch per page load answers "is anything of mine in flight?", and
      // anything that starts after that goes through refresh() or watch().
      if (!busy && watchersRef.current === 0) {
        idle = true;
        return;
      }
      idle = false;
      timerRef.current = setTimeout(tick, busy ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    };

    // `force` fetches now even when the loop is already running. Without it a
    // nudge was ignored whenever an importer was on screen (the loop is then
    // sitting in its 30-second idle wait, not stopped), so a freshly started
    // import stayed off the ticker for up to half a minute — and one that
    // finished inside that window never appeared, nor was announced.
    wakeRef.current = (force = false) => {
      if (stopped) return;
      if (!idle && !force) return;
      idle = false;
      void tick();
    };

    void tick();

    return () => {
      stopped = true;
      cancelledRef.current = true;
      wakeRef.current = null;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [fetchActive, fetchSpec]);

  const refresh = useCallback(() => {
    // Fetches immediately, restarting the loop if it had gone quiet —
    // starting an import is exactly when polling needs to resume.
    if (wakeRef.current) wakeRef.current(true);
    else {
      void fetchActive();
      void fetchSpec();
    }
  }, [fetchActive, fetchSpec]);

  // A finished announcement must clear itself even when nothing is running,
  // because the poll drops to its idle cadence the moment the last job ends.
  useEffect(() => {
    if (finishedImports.length === 0) return;
    const timer = setTimeout(() => {
      const now = Date.now();
      setFinishedImports((prev) =>
        prev.filter((job) => now - job.finished_at < FINISHED_VISIBLE_MS),
      );
    }, FINISHED_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [finishedImports]);

  // Same self-clearing guarantee for a finished spec announcement.
  useEffect(() => {
    if (specFinishedImports.length === 0) return;
    const timer = setTimeout(() => {
      const now = Date.now();
      setSpecFinishedImports((prev) =>
        prev.filter((job) => now - job.finished_at < FINISHED_VISIBLE_MS),
      );
    }, FINISHED_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [specFinishedImports]);

  // The spreadsheet (ImportBatch) jobs and the spec-document jobs are tracked
  // separately but presented as one list, so the ticker and rings need no
  // knowledge of the two sources.
  const mergedActive = useMemo(
    () => [...activeImports, ...specActiveImports],
    [activeImports, specActiveImports],
  );
  const mergedFinished = useMemo(
    () => [...finishedImports, ...specFinishedImports],
    [finishedImports, specFinishedImports],
  );

  const value = useMemo(
    () => ({
      activeImports: mergedActive,
      finishedImports: mergedFinished,
      refresh,
      watch,
    }),
    [mergedActive, mergedFinished, refresh, watch],
  );

  return (
    <ImportJobContext.Provider value={value}>{children}</ImportJobContext.Provider>
  );
};

export function useImportJobs(): ImportJobContextValue {
  return useContext(ImportJobContext);
}

export default ImportJobContext;
